import React, { useCallback, useMemo, useEffect } from 'react';
import { Form } from '@remix-run/react';
import { reservationStore } from '../../state/reservation-store';
import { trackButtonClick, ButtonTag } from '../../state/analytics';
import { BookingFormLayout } from './BookingFormLayout';
import { StepIndicator } from './step-indicator';
import { RoomSelectionStep } from './room-selection';
import { DateGuestStep } from './date-guest-selection';
import { GuestDetailsStep } from './guest-details';
import { BookingSummary } from './booking-summary/BookingSummary';
import { BookingSummaryMobile } from './booking-summary/BookingSummaryMobile';
import { useBookingFormV4 } from './hooks/useBookingFormV4';
import { useStepNavigation } from './hooks/useStepNavigation';
import { BrandThemeProvider } from './brand-theme';
import { offeringsState } from '../../state/offerings';
import {
  isDefaultPackage,
  getPackageSupplementaryCost,
} from '../../state/packages';
import type {
  BookingFormV4Props,
  StepIndicatorItem,
  TentType,
} from './types';
import type { NivaasPackage } from '../../types/package';

/**
 * Main V4 booking form orchestrator.
 * Implements modern UX patterns with horizontal step navigation,
 * two-column layout, and sticky booking summary.
 * 
 * Flow: Room Selection (with package upgrade) -> Dates & Guests -> Guest Details
 */
export const BookingFormV4: React.FC<BookingFormV4Props> = ({
  formId,
  tents,
  packages,
  onNavigateToPayment,
  minDate,
  maxDate,
  bathingDates,
  messages,
  brand = 'camp67',
}) => {
  // Initialize booking form state
  const booking = useBookingFormV4(formId);

  // Sort packages: DEFAULT first, then CUSTOM sorted by supplementary cost
  const sortedPackages = useMemo(() => {
    return [...packages].sort((a, b) => {
      // DEFAULT packages come first
      const aIsDefault = isDefaultPackage(a);
      const bIsDefault = isDefaultPackage(b);
      if (aIsDefault !== bIsDefault) {
        return aIsDefault ? -1 : 1;
      }
      // Within same type, sort by supplementary cost (ascending)
      const priceA = getPackageSupplementaryCost(a)?.amount || 0;
      const priceB = getPackageSupplementaryCost(b)?.amount || 0;
      return priceA - priceB;
    });
  }, [packages]);

  // Auto-select the DEFAULT package if none is selected
  useEffect(() => {
    if (sortedPackages.length > 0 && !booking.selectedPackage) {
      const defaultPkg = sortedPackages.find(isDefaultPackage);
      if (defaultPkg) {
        booking.selectPackage(defaultPkg);
      }
    }
  }, [sortedPackages, booking.selectedPackage, booking.selectPackage]);

  // Step navigation (no longer needs hasPackages since package selection is integrated)
  const navigation = useStepNavigation({
    isRoomStepComplete: booking.isRoomStepComplete,
    isDateGuestStepComplete: booking.isDateGuestStepComplete,
    isGuestDetailsStepComplete: booking.isGuestDetailsStepComplete,
  });

  // Initialize guest details when entering step 3
  useEffect(() => {
    if (navigation.currentStep === 'guest-details') {
      reservationStore.initialiseGuestDetails();
    }
  }, [navigation.currentStep]);

  // Build step indicator items (3 steps only)
  const steps: StepIndicatorItem[] = useMemo(() => {
    return [
      {
        id: 'room-selection',
        label: messages.stepIndicator.roomSelection,
        shortLabel: 'Rooms',
      },
      {
        id: 'date-guest',
        label: messages.stepIndicator.dateGuest,
        shortLabel: 'Dates',
      },
      {
        id: 'guest-details',
        label: messages.stepIndicator.guestDetails,
        shortLabel: 'Details',
      },
    ];
  }, [messages.stepIndicator]);

  // Build offering names map for package cards "Available for" display
  const offeringNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const tent of tents) {
      // Use the full nivaas offering ID format
      const offeringId = `nivaas:camp67:room-offering/${tent.tentType}`;
      map.set(offeringId, tent.title);
      // Also add just the tent type in case the ID format is different
      map.set(tent.tentType, tent.title);
    }
    return map;
  }, [tents]);

  // Build offering slug to ID map for rate card lookup
  const offeringSlugToId = useMemo(() => {
    const map = new Map<string, string>();
    for (const offering of offeringsState.offerings) {
      if (offering.slug) {
        map.set(offering.slug, offering.id);
      }
    }
    return map;
  }, []);

  // Get brand config for occupancy pricing support
  const brandConfig = reservationStore.getBrandConfig();
  const supportsOccupancyPricing = brandConfig?.supportsOccupancyPricing ?? false;

  // Handle package selection
  const handleSelectPackage = useCallback(
    (pkg: NivaasPackage | null) => {
      booking.selectPackage(pkg);
      trackButtonClick(ButtonTag.SELECT_PACKAGE, { packageId: pkg?.id ?? 'base' });
    },
    [booking],
  );

  // Handle room quantity change
  const handleRoomQuantityChange = useCallback(
    (type: TentType, delta: number) => {
      if (delta > 0) {
        booking.addRoom(type);
        trackButtonClick(ButtonTag.ADD_ACCOMMODATION, { tentType: type });
      } else {
        booking.removeRoom(type);
        trackButtonClick(ButtonTag.SUBTRACT_ACCOMMODATION, { tentType: type });
      }
    },
    [booking],
  );

  // Handle proceeding to next step or payment
  const handleProceed = useCallback(() => {
    if (navigation.currentStep === 'guest-details') {
      // Validate and proceed to payment
      const isValid = booking.validate();
      if (isValid) {
        onNavigateToPayment();
      }
    } else {
      navigation.goToNextStep();
    }
  }, [navigation, booking, onNavigateToPayment]);

  // Can proceed validation based on current step
  const canProceed = useMemo(() => {
    switch (navigation.currentStep) {
      case 'room-selection':
        return booking.isRoomStepComplete;
      case 'date-guest':
        return booking.isDateGuestStepComplete;
      case 'guest-details':
        return booking.canSubmit;
      default:
        return false;
    }
  }, [navigation.currentStep, booking]);

  // Get tent config for summary
  const getTentConfig = useCallback(
    (type: TentType) => {
      const tent = tents.find((t) => t.tentType === type);
      if (!tent) return null;
      return { title: tent.title, imageUrl: tent.imageUrl };
    },
    [tents],
  );

  // Render current step content
  const renderStepContent = () => {
    switch (navigation.currentStep) {
      case 'room-selection':
        return (
          <RoomSelectionStep
            tents={tents}
            roomCounts={booking.roomCounts}
            onQuantityChange={handleRoomQuantityChange}
            availableTentTypes={booking.availableTentTypes}
            messages={messages.roomSelection}
            packages={sortedPackages}
            selectedPackage={booking.selectedPackage}
            onSelectPackage={handleSelectPackage}
            totalGuestCount={booking.adults + booking.children}
            supportsOccupancyPricing={supportsOccupancyPricing}
            offeringSlugToId={offeringSlugToId}
          />
        );

      case 'date-guest':
        return (
          <DateGuestStep
            startDate={booking.startDate}
            endDate={booking.endDate}
            adults={booking.adults}
            children={booking.children}
            maxCapacity={booking.maxCapacity}
            minDate={minDate}
            maxDate={maxDate}
            availableDates={booking.availableDates}
            rooms={booking.rooms}
            onStartDateSelect={(date) => booking.setStartDate(date)}
            onEndDateSelect={(date) => booking.setEndDate(date)}
            onClearDates={booking.clearDates}
            onAdultsChange={booking.setAdults}
            onChildrenChange={booking.setChildren}
            bathingDates={bathingDates}
            messages={messages.dateGuest}
          />
        );

      case 'guest-details':
        return (
          <GuestDetailsStep
            guests={booking.guests}
            primaryContactId={booking.primaryContactId}
            primaryEmail={booking.primaryEmail}
            primaryPhone={booking.primaryPhone}
            countryCode={booking.countryCode}
            onGuestChange={booking.updateGuest}
            onPrimaryContactChange={booking.setPrimaryContact}
            onEmailChange={booking.setEmail}
            onPhoneChange={booking.setPhone}
            onCountryCodeChange={booking.setCountryCode}
            emailError={booking.emailError}
            phoneError={booking.phoneError}
            messages={messages.guestDetails}
          />
        );

      default:
        return null;
    }
  };

  // Summary props
  const summaryProps = {
    selectedPackage: booking.selectedPackage,
    selectedRooms: booking.rooms,
    dateRange: {
      start: booking.startDate?.toISOString() ?? '',
      end: booking.endDate?.toISOString() ?? '',
    },
    guestCounts: {
      adults: booking.adults,
      children: booking.children,
    },
    pricing: booking.pricing,
    currentStep: navigation.currentStep,
    onProceed: handleProceed,
    canProceed,
    messages: messages.summary,
    getTentConfig,
  };

  return (
    <BrandThemeProvider brand={brand}>
      <Form id={formId} className="py-4 md:py-6">
        {/* Step indicator */}
        <div className="mb-6 md:mb-8 px-4 md:px-0">
          <StepIndicator
            steps={steps}
            currentStep={navigation.currentStep}
            completedSteps={navigation.completedSteps}
            onStepClick={(step) => navigation.goToStep(step)}
          />
        </div>

        {/* Main layout */}
        <BookingFormLayout
          sidebar={<BookingSummary {...summaryProps} />}
          mobileBottomBar={<BookingSummaryMobile {...summaryProps} />}
        >
          {/* Step content */}
          <div className="space-y-6">{renderStepContent()}</div>
        </BookingFormLayout>
      </Form>
    </BrandThemeProvider>
  );
};

export default BookingFormV4;
